import { formatSummaryFeature } from '~app/pages/Pricing/lib/PackageFeatures';
import { SETTING_UNLIMITED } from '~app/pages/Pricing/transformers/summary/rules/featureRules';
import { SummaryFeatureRuleMap } from '~app/pages/Pricing/transformers/summary/types';
import { Packages } from '~shared/constants/pricing';
import { DisplayFeatureStatic } from '~shared/types/pricing/features';

const FeatureRules: SummaryFeatureRuleMap = {
  // Add feature rule overrides below
  grow_2792_user: (_, { packageName }) => {
    let obj: DisplayFeatureStatic = formatSummaryFeature('grow_2792_single_user');

    if (packageName) {
      if (
        [Packages.TeamAdvantage, Packages.AdvantageAnnual, Packages.PremierAnnual, Packages.TeamPremier].includes(
          packageName
        )
      ) {
        obj = formatSummaryFeature('grow_2792_team_collaboration_user');
      }
      if (
        [Packages.FormsAnnual, Packages.FormsMonthly, Packages.AdvantageMonthly, Packages.Basic].includes(packageName)
      ) {
        obj = formatSummaryFeature('grow_2792_forms_advantage_team');
      }

      if ([Packages.Enterprise].includes(packageName)) {
        obj = formatSummaryFeature('grow_2792_advanced_team_collaboration_user');
      }
    }

    return obj;
  },
  grow_2792_survey_create_limit: ({ survey_create_limit, create_question_limit }) => {
    let obj: DisplayFeatureStatic = {};

    if (survey_create_limit?.setting === SETTING_UNLIMITED) {
      obj = formatSummaryFeature('grow_2792_survey_create_limit');
    } else if (create_question_limit?.setting === SETTING_UNLIMITED) {
      obj = formatSummaryFeature('create_question_limit');
    }

    return obj;
  },
  unlimited_questions: () => {
    return formatSummaryFeature('grow_2792_unlimited_questions');
  },
  response_count: ({ base_response_count, num_free_responses }, { locale, overageCost = '' }) => {
    let obj: DisplayFeatureStatic = formatSummaryFeature('grow_2792_response_count', {
      amount: base_response_count?.amount?.toLocaleString(locale) as string,
      overageCost,
    });

    // Annual packages
    if (base_response_count?.amount) {
      obj = formatSummaryFeature('grow_2792_base_response_count', {
        amount: base_response_count.amount.toLocaleString(locale),
        overageCost,
      });

      // Basic/Standard monthly
    } else if (num_free_responses?.amount) {
      obj = formatSummaryFeature('num_free_responses', {
        amount: num_free_responses.amount.toLocaleString(locale),
        overageCost,
      });

      // Enterprise
    } else if (num_free_responses?.setting === SETTING_UNLIMITED) {
      obj = formatSummaryFeature('grow_2792_custom_response_limits');
    }

    return obj;
  },

  grow_2792_question_types: (_, { packageName }) => {
    let obj: DisplayFeatureStatic = {};
    if (packageName && [Packages.Basic].includes(packageName)) {
      obj = formatSummaryFeature('grow_2792_basic_question_types');
    } else {
      obj = formatSummaryFeature(
        packageName && [Packages.FormsAnnual, Packages.FormsMonthly].includes(packageName)
          ? 'grow_2792_forms_question_types'
          : 'grow_2792_all_question_types_t1_t2'
      );
    }
    return obj;
  },
  support: (_, { packageName }) => {
    let obj = formatSummaryFeature(
      packageName && [Packages.FormsMonthly, Packages.FormsAnnual, Packages.Basic].includes(packageName)
        ? 'grow_2792_support'
        : 'priority_email_support'
    );
    if (packageName && [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier].includes(packageName)) {
      obj = formatSummaryFeature('grow_2792_phone_support');
    }
    if (
      packageName &&
      [Packages.AdvantageMonthly, Packages.TeamAdvantage, Packages.AdvantageAnnual].includes(packageName)
    ) {
      obj = formatSummaryFeature('grow_2792_priority_email_support');
    }
    return obj;
  },
  grow_2792_analyze_export_enabled: (_, { packageName }) => {
    if (packageName && [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier].includes(packageName)) {
      return formatSummaryFeature('all_data_exports');
    }
    return formatSummaryFeature('grow_2792_analyze_export_enabled');
  },
  grow_2792_website_embed: () => formatSummaryFeature('grow_2792_website_embed'),
  grow_2792_zapier_integrations: () => formatSummaryFeature('grow_2792_zapier_integrations'),
  skip_logic: (_, { packageName }) => {
    let obj = formatSummaryFeature('grow_2792_skip_logic');
    if (
      packageName &&
      [Packages.Enterprise, Packages.PremierAnnual, Packages.PremierMonthly, Packages.TeamPremier].includes(packageName)
    ) {
      obj = formatSummaryFeature('create_piping_enabled');
    }
    return obj;
  },
  grow_2792_survey_branding: (_, { packageName }) => {
    let obj = formatSummaryFeature(
      packageName && [Packages.FormsAnnual, Packages.FormsMonthly].includes(packageName)
        ? 'grow_2792_create_custom_theme_enabled'
        : 'survey_branding'
    );
    if (
      packageName &&
      [Packages.Enterprise, Packages.PremierAnnual, Packages.PremierMonthly, Packages.TeamPremier].includes(packageName)
    ) {
      obj = formatSummaryFeature('collector_white_label_enabled');
    }
    return obj;
  },
  grow_2792_text_analysis: () => formatSummaryFeature('grow_2792_text_analysis'),
  grow_2792_multilingual: () => formatSummaryFeature('multilingual'),
  grow_2792_a_b_testing: () => formatSummaryFeature('grow_2792_a_b_testing'),
  grow_2792_basic_create_question_limit: () =>
    formatSummaryFeature('grow_2792_basic_create_question_limit', { amount: 10 }),
  grow_2792_all_question_types: () => formatSummaryFeature('grow_2792_all_question_types'),
  grow_2792_basic_base_response_count: () =>
    formatSummaryFeature('grow_2792_basic_base_response_count', { amount: 25 }),
  grow_2792_support: () => formatSummaryFeature('grow_2792_support'),
};

export default FeatureRules;
